import { useMutation } from '@tanstack/react-query'
import Button from 'components/button'
import { ArrowBackRounded, ArrowForwardRounded } from 'components/icons'
import Modal from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import type { PDFDocumentProxy } from 'pdfjs-dist'
// Let Vite resolve the worker as a hashed asset — same pattern as pdfModalContent.
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import './pdfFormEditor.css'
import { SizableText, Spinner, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { usePurchaserContext } from '../purchaserProvider'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

interface Props {
    open: boolean
    purchaserId: string
    onClose: () => void
    /** Called after the envelope is created — receives the embedded DocuSign sender-view URL */
    onCreated: (editUrl: string) => void
}

const bytesToBase64 = (bytes: Uint8Array): string => {
    let binary = ''
    const chunk = 0x8000
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
    }
    return btoa(binary)
}

/**
 * In-app WYSIWYG editor for the agent appointment form (DocuSign flow).
 * Loads the prefilled-but-editable PDF, renders its AcroForm fields as
 * interactive inputs (react-pdf `renderForms`), lets the agent edit any field,
 * then serialises the edits via pdf.js `saveDocument()` and creates + sends the
 * DocuSign envelope (signatures only).
 */
const PdfFormEditorModal = ({ open, purchaserId, onClose, onCreated }: Props) => {
    const trpc = useTRPC()
    const showSnack = useShowSnack()
    const purchaserContext = usePurchaserContext()
    const setPurchaser = purchaserContext?.setPurchaser

    const prepareMutation = useMutation(trpc.esign.prepareAgentAppointmentForm.mutationOptions())
    const createMutation = useMutation(trpc.esign.createAgentAppointmentForm.mutationOptions())

    const [pdfFile, setPdfFile] = useState<string>()
    const [numPages, setNumPages] = useState<number>()
    const [pageNumber, setPageNumber] = useState(1)
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const pdfProxyRef = useRef<PDFDocumentProxy | null>(null)

    // Load the prefilled, editable PDF when the modal opens; reset on close.
    useEffect(() => {
        if (!open) {
            setPdfFile(undefined)
            setNumPages(undefined)
            setPageNumber(1)
            pdfProxyRef.current = null
            return
        }
        let cancelled = false
        setLoading(true)
        prepareMutation
            .mutateAsync({ purchaserId })
            .then(res => {
                if (!cancelled) setPdfFile(`data:application/pdf;base64,${res.pdfBase64}`)
            })
            .catch((e: any) => {
                if (!cancelled) {
                    showSnack(e?.message || 'Failed to prepare form', 'danger')
                    onClose()
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, purchaserId])

    const options = useMemo(() => ({ cMapUrl: '/cmaps/', cMapPacked: true }), [])

    const handleSendToDocusign = async () => {
        const proxy = pdfProxyRef.current
        if (!proxy) {
            showSnack('PDF is still loading', 'danger')
            return
        }
        setSubmitting(true)
        try {
            // Serialise the agent's edited form-field values into the PDF
            const editedBytes = await proxy.saveDocument()
            const pdfBase64 = bytesToBase64(editedBytes)

            // Create the DocuSign envelope, then hand off to its embedded sender view
            const { updatedPurchaser, editUrl } = await createMutation.mutateAsync({
                purchaserId,
                pdfBase64,
            })
            setPurchaser?.(updatedPurchaser)
            onCreated(editUrl)
            onClose()
        } catch (e: any) {
            showSnack(e?.message || 'Failed to create envelope', 'danger')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            open={open}
            onOpenChange={isOpen => {
                if (!isOpen && !submitting) onClose()
            }}
            width="90vw"
            maxWidth={920}
            height="90vh"
        >
            <YStack
                gap="$3"
                flex={1}
                overflow="hidden"
                paddingBottom="$2"
            >
                <SizableText
                    size="$6"
                    fontWeight="700"
                    letterSpacing={-0.3}
                >
                    Review & edit the form
                </SizableText>
                <SizableText
                    size="$2"
                    color="$gray11"
                >
                    Edit any field below, then send to DocuSign.
                </SizableText>

                {/* PDF editing surface */}
                <View
                    flex={1}
                    overflow="auto"
                    backgroundColor="$gray3"
                    borderRadius="$4"
                    alignItems="center"
                    paddingVertical="$3"
                >
                    {loading || !pdfFile ? (
                        <View
                            flex={1}
                            justifyContent="center"
                            alignItems="center"
                            minHeight={300}
                        >
                            <Spinner size="large" />
                        </View>
                    ) : (
                        <div className="bf-pdf-editor">
                            <Document
                                file={pdfFile}
                                options={options}
                                onLoadSuccess={pdf => {
                                    pdfProxyRef.current = pdf
                                    setNumPages(pdf.numPages)
                                }}
                                onLoadError={error => console.error('Error loading PDF:', error)}
                            >
                                <Page
                                    pageNumber={pageNumber}
                                    scale={1.33}
                                    renderForms
                                    renderTextLayer={false}
                                />
                            </Document>
                        </div>
                    )}
                </View>

                {/* Pagination + actions */}
                <XStack
                    position="relative"
                    alignItems="center"
                    justifyContent="center"
                    gap="$3"
                    flexShrink={0}
                >
                    <Button
                        variant="outlined"
                        position="absolute"
                        left={0}
                        onPress={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>

                    <XStack
                        gap="$3"
                        alignItems="center"
                        margin="auto"
                    >
                        <Button
                            size="$3"
                            circular
                            chromeless
                            disabled={pageNumber <= 1}
                            opacity={pageNumber <= 1 ? 0.4 : 1}
                            onPress={() => setPageNumber(p => Math.max(1, p - 1))}
                        >
                            <ArrowBackRounded size={18} />
                        </Button>
                        <SizableText userSelect="none">
                            Page {pageNumber} of {numPages ?? '–'}
                        </SizableText>
                        <Button
                            size="$3"
                            circular
                            chromeless
                            disabled={pageNumber >= (numPages ?? 1)}
                            opacity={pageNumber >= (numPages ?? 1) ? 0.4 : 1}
                            onPress={() => setPageNumber(p => Math.min(numPages ?? 1, p + 1))}
                        >
                            <ArrowForwardRounded size={18} />
                        </Button>
                    </XStack>

                    <Button
                        theme="primary"
                        position="absolute"
                        right={0}
                        disabled={loading || submitting || !pdfFile}
                        icon={submitting ? <Spinner /> : undefined}
                        onPress={handleSendToDocusign}
                    >
                        Send to DocuSign
                    </Button>
                </XStack>
            </YStack>
        </Modal>
    )
}

export default PdfFormEditorModal
